import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import { settingsAPI, categoriesAPI } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';

export const Footer = () => {
  const { data: settingsRes } = useApiQuery(['settings'], () => settingsAPI.get());
  const { data: categoriesRes } = useApiQuery(['categories'], () => categoriesAPI.getAll());

  const fallbackSettings = {
    phone: '',
    email: '',
    address: 'Kuwait City, Kuwait',
    facebook_url: '',
    instagram_url: '',
    site_description: 'For Wholesale And Retail Trade. Browse our collection of 1600+ premium products and place your orders with ease.',
  };

  const settings = { ...fallbackSettings, ...(settingsRes?.data || {}) };
  const categories = (categoriesRes?.data || []).slice(0, 6);

  const quickLinks = [
    { to: '/products', label: 'All Products' },
    { to: '/cart', label: 'Shopping Cart' },
    { to: '/my-orders', label: 'My Orders' },
    { to: '/track-order', label: 'Track Order' },
    { to: '/contact', label: 'Contact Us' },
  ];

  const fallbackCategories = ['Desktops', 'Laptops', 'RAM & Memory', 'Monitors', 'Storage', 'Accessories'];

  return (
    <footer className="bg-[#0f172a] text-slate-400" data-testid="footer">
      {/* ── Main grid ───────────────────────────────────────────── */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <img
                src="/gulf-star-logo.jpeg"
                alt="Gulf Star"
                className="h-11 w-11 object-contain rounded-xl"
              />
              <span
                className="text-xl font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Gulf Star
              </span>
            </Link>
            <p className="text-sm leading-relaxed mb-6">{settings.site_description}</p>
            <div className="flex gap-2.5">
              {[
                { Icon: Facebook, href: settings.facebook_url || '#' },
                { Icon: Instagram, href: settings.instagram_url || '#' },
                { Icon: Twitter, href: '#' },
                { Icon: Youtube, href: '#' },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-slate-800 hover:bg-[#8acb95] rounded-lg flex items-center justify-center transition-all duration-200 group"
                >
                  <Icon className="w-4 h-4 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3
              className="text-white font-semibold mb-5 text-xs uppercase tracking-widest"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm hover:text-[#8acb95] transition-colors flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-[#8acb95] rounded-full flex-shrink-0" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3
              className="text-white font-semibold mb-5 text-xs uppercase tracking-widest"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Categories
            </h3>
            <ul className="space-y-3">
              {(categories.length > 0 ? categories.map(c => c.name) : fallbackCategories).map((cat) => (
                <li key={cat}>
                  <Link
                    to={`/products?category=${encodeURIComponent(cat)}`}
                    className="text-sm hover:text-[#8acb95] transition-colors flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-[#8acb95] rounded-full flex-shrink-0" />
                    {cat}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3
              className="text-white font-semibold mb-5 text-xs uppercase tracking-widest"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Contact Us
            </h3>
            <ul className="space-y-4">
              {settings.phone && (
                <li>
                  <a
                    href={`tel:${settings.phone}`}
                    className="flex items-start gap-3 text-sm hover:text-[#8acb95] transition-colors"
                  >
                    <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-3.5 h-3.5 text-[#8acb95]" />
                    </div>
                    <span className="mt-1">{settings.phone}</span>
                  </a>
                </li>
              )}
              {settings.email && (
                <li>
                  <a
                    href={`mailto:${settings.email}`}
                    className="flex items-start gap-3 text-sm hover:text-[#8acb95] transition-colors"
                  >
                    <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-3.5 h-3.5 text-[#8acb95]" />
                    </div>
                    <span className="mt-1 break-all">{settings.email}</span>
                  </a>
                </li>
              )}
              {settings.address && (
                <li className="flex items-start gap-3 text-sm">
                  <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-[#8acb95]" />
                  </div>
                  <span className="mt-1">{settings.address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* ── Bottom strip ───────────────────────────────────────── */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-5 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Gulf Star Trading. All rights reserved.
          </p>
          <p className="text-xs text-slate-500">
            For Wholesale &amp; Retail Trade · Kuwait
          </p>
        </div>
      </div>
    </footer>
  );
};
